import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMainLinks } from '@/hooks/use-main-links';
import { useAuth } from '@/hooks/use-auth';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Plus, ExternalLink, Edit, Trash2, Eye, BarChart3 } from 'lucide-react';
import { CreateMainLinkDialog } from '@/components/link-short/CreateMainLinkDialog';
import { Badge } from '@/components/ui/badge';
import { toast } from '@/hooks/use-toast';

export function LinkShortPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { mainLinks, loading, deleteMainLink } = useMainLinks();
  const [showCreateDialog, setShowCreateDialog] = useState(false);

  if (!user) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">Faça login para gerenciar seus links.</p>
      </div>
    );
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Tem certeza que deseja excluir este link? Esta ação não pode ser desfeita.')) {
      return;
    }
    
    await deleteMainLink(id);
  };

  const handleEdit = (id: string) => {
    navigate(`/link-short/${id}`);
  };

  const copyToClipboard = (slug: string) => {
    const url = `${window.location.origin}/${slug}`;
    navigator.clipboard.writeText(url);
    toast.success("Link copiado para a área de transferência!");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold">Link Curto</h1>
          <p className="text-muted-foreground">
            Crie páginas personalizadas com seus links importantes
          </p>
        </div>
        <Button onClick={() => setShowCreateDialog(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Novo Link
        </Button>
      </div>

      {mainLinks.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <div className="text-center">
              <h3 className="text-lg font-semibold mb-2">Nenhum link criado ainda</h3>
              <p className="text-muted-foreground mb-4">
                Crie seu primeiro link personalizado para compartilhar com seus contatos
              </p>
              <Button onClick={() => setShowCreateDialog(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Criar Primeiro Link
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {mainLinks.map((mainLink) => (
            <Card key={mainLink.id} className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span className="truncate">{mainLink.title}</span>
                  <Badge variant={mainLink.is_active ? 'default' : 'secondary'}>
                    {mainLink.is_active ? 'Ativo' : 'Inativo'}
                  </Badge>
                </CardTitle>
                {mainLink.description && (
                  <p className="text-sm text-muted-foreground line-clamp-2">
                    {mainLink.description}
                  </p>
                )}
              </CardHeader>
              
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <ExternalLink className="h-4 w-4" />
                    <span 
                      className="truncate cursor-pointer hover:text-primary"
                      onClick={() => copyToClipboard(mainLink.slug)}
                    >
                      /{mainLink.slug}
                    </span>
                  </div>

                  <div className="flex items-center gap-4 text-sm text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <BarChart3 className="h-4 w-4" />
                      <span>{mainLink.clicks} cliques</span>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1"
                      onClick={() => window.open(`/${mainLink.slug}`, '_blank')}
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      Ver
                    </Button>
                    
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1"
                      onClick={() => handleEdit(mainLink.id)}
                    >
                      <Edit className="h-4 w-4 mr-1" />
                      Editar
                    </Button>
                    
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-red-600 hover:text-red-700"
                      onClick={() => handleDelete(mainLink.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <CreateMainLinkDialog
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
      />
    </div>
  );
}
